import styles from "./AppDatePicker.module.scss";

import { type ReactNode, useCallback, useMemo, useState } from "react";

import { toZonedTime } from "date-fns-tz";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import { useTranslation } from "react-i18next";

import { AppLoadingIndicator } from "../AppLoadingIndicator/AppLoadingIndicator";

import { AppChevron } from "./components/AppChevron";
import { AppDayButton } from "./components/AppDayButton";
import { AppMonthCaption } from "./components/AppMonthCaption";
import { AppWeekday } from "./components/AppWeekday";
import { MONTH_KEYS } from "./consts/MonthKeys";
import { MonthSubtitleContext } from "./context/MonthSubtitleContext";
import { getIsWholeMonthDisabled } from "./utils/getIsWholeMonthDisabled";
import { getWeekdayNameFormat } from "./utils/getWeekdayNameFormat";
import { utcDateFromLocalFields } from "./utils/utcDateFromLocalFields";

const rdpDefaultClassNames = getDefaultClassNames();

type AppDatePickerProps = {
  defaultSelected?: Date;
  disabled?: Date[];
  onSelectDate?: (date: Date) => void;
  onMonthChange?: (date: Date) => void;
  isDisabledDate?: (date: Date) => boolean;
  isLoading?: boolean;
  forceDisabledMonthState?: boolean;
  /**
   * IANA timezone string (e.g. "America/Los_Angeles") used to determine which
   * day is "today". When omitted, DayPicker falls back to its own default,
   * which resolves to the UTC date (because `timeZone="UTC"` is hardcoded).
   */
  userTimezone?: string;
  // Renders below the month label inside the caption; used to surface the
  // trial end-date copy (MEM-520).
  monthSubtitle?: ReactNode;
  // Latest navigable month; DayPicker disables the forward chevron at this
  // boundary. Used to clamp trial members to their trial end month (MEM-520).
  endMonth?: Date;
};

export const AppDatePicker = ({
  defaultSelected,
  disabled,
  onSelectDate,
  onMonthChange,
  isLoading,
  isDisabledDate,
  forceDisabledMonthState,
  userTimezone,
  monthSubtitle,
  endMonth,
}: AppDatePickerProps) => {
  const [selected, setSelected] = useState<Date | undefined>(defaultSelected);
  const [focusedMonth, setFocusedMonth] = useState<Date>(() => new Date());
  const { t } = useTranslation();

  const todayInUserTimezone = useMemo(() => {
    if (!userTimezone) return undefined;
    // toZonedTime shifts the *local* fields to represent the wall clock in
    // the target timezone. Because DayPicker is pinned to timeZone="UTC",
    // it reads the *UTC* fields of the Date. We therefore need a Date whose
    // UTC year/month/day match the wall-clock date in the user's timezone.
    // Note: this value is only recomputed when userTimezone changes — it does
    // not auto-advance at midnight. This matches DayPicker's default behaviour.
    // eslint-disable-next-line react-hooks/purity
    const zoned = toZonedTime(new Date(), userTimezone);
    return utcDateFromLocalFields(
      zoned.getFullYear(),
      zoned.getMonth(),
      zoned.getDate(),
    );
  }, [userTimezone]);

  const isWholeMonthDisabled = useMemo(() => {
    if (forceDisabledMonthState) {
      return forceDisabledMonthState;
    }
    return getIsWholeMonthDisabled(disabled, focusedMonth);
  }, [disabled, focusedMonth, forceDisabledMonthState]);

  const handleOnSelectDate = useCallback(
    (date: Date) => {
      setSelected(date);
      onSelectDate?.(date);
    },
    [onSelectDate],
  );

  const handleOnMonthChange = useCallback(
    (date: Date) => {
      setFocusedMonth(date);
      onMonthChange?.(date);
    },
    [onMonthChange],
  );

  return (
    <div className={styles.container} data-testid="app-date-picker">
      <MonthSubtitleContext.Provider value={monthSubtitle}>
        <DayPicker
          required
          animate
          navLayout="around"
          mode="single"
          selected={selected}
          onSelect={handleOnSelectDate}
          onMonthChange={handleOnMonthChange}
          today={todayInUserTimezone}
          timeZone={"UTC"} // Kept as UTC to avoid calendar re-render on timezone change. The `today` prop above corrects the "today" marker.
          endMonth={endMonth}
          formatters={{
            formatWeekdayName: getWeekdayNameFormat,
          }}
          components={{
            Weekday: AppWeekday,
            MonthCaption: AppMonthCaption,
            DayButton: AppDayButton,
            Chevron: AppChevron,
          }}
          classNames={{
            disabled: styles.disabledDay,
            ...(monthSubtitle
              ? {
                  month_caption: `${rdpDefaultClassNames.month_caption} ${styles.monthCaptionAutoHeight}`,
                }
              : {}),
          }}
          disabled={isDisabledDate ?? disabled}
        />
      </MonthSubtitleContext.Provider>
      {isWholeMonthDisabled && (
        <div
          className={`${styles.overlayContainer} ${styles.overlayEmptyMonth}`}
        >
          {t("book.calendar.noTimesThisMonth", {
            month: t(MONTH_KEYS[focusedMonth.getMonth()]),
          })}
        </div>
      )}

      {isLoading && (
        <div className={styles.overlayContainer}>
          <AppLoadingIndicator />
        </div>
      )}
    </div>
  );
};
